import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Terminal, ChevronDown, ChevronUp, RefreshCw, Wifi, WifiOff } from 'lucide-react'

export default function DebugPanel({ debugInfo, isArchiveMode, checkSyncStatus, forcePeerDiscovery, peerCount, networkStatus }) {
  const [isExpanded, setIsExpanded] = useState(false)

  const formatDebugInfo = (info) => {
    if (typeof info === 'string') {
      return info
    }
    return JSON.stringify(info, null, 2)
  }

  return (
    <motion.div
      className={`
        bg-transparent border border-current rounded-lg mt-3xl motion-luxury
        ${isArchiveMode ? 'border-white' : ''}
      `}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.6 }}
    >
      {/* Header */}
      <div className="w-full p-xl flex items-center justify-between">
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-3 text-left motion-luxury hover:bg-current/5 flex-1"
          whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
        >
          <Terminal className="w-5 h-5" />
          <h3 className="font-display text-base font-bold tracking-tight">
            System Status
          </h3>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.button>

        {/* Sync Status Indicator */}
        <div className="flex items-center gap-3">
          {/* Network Status */}
          <div className="flex items-center gap-2 text-sm">
            {networkStatus === 'connected' ? (
              <Wifi className="w-4 h-4 text-green-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
            <span className="font-mono text-xs">
              {peerCount} peers
            </span>
          </div>

        {/* Manual Sync Button */}
        <motion.button
          onClick={checkSyncStatus}
          className="p-2 rounded-lg bg-current/10 hover:bg-current/20 motion-luxury"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Check sync status"
        >
          <RefreshCw className="w-4 h-4" />
        </motion.button>

        {/* Force Peer Discovery Button */}
        <motion.button
          onClick={forcePeerDiscovery}
          className={`
            px-3 py-2 rounded-lg text-xs font-mono motion-luxury
            ${peerCount === 0 
              ? 'bg-red-500/20 text-red-600 border border-red-500/30 hover:bg-red-500/30' 
              : 'bg-current/10 hover:bg-current/20'
            }
          `}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title="Force peer discovery for cross-network sync"
        >
          <div className="flex items-center gap-1">
            <Wifi className="w-3 h-3" />
            <span>Discover</span>
          </div>
        </motion.button>
        </div>
      </div>

      {/* Debug Info */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.33, 1, 0.68, 1] }}
            className="overflow-hidden"
          >
            <div className="px-xl pb-xl">
              <pre className={`
                bg-black text-white p-md rounded-lg overflow-x-auto text-[11px]
                font-body leading-relaxed motion-luxury
                ${isArchiveMode ? 'bg-white text-black' : ''}
              `}>
                {formatDebugInfo(debugInfo)}
              </pre>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}